class TrackingService {
  constructor() {
    this.defaultEvent = 'widget.display';
    this.allPendingEvents = []; // Collection temporaire de tous les événements
    this.processingTimeout = null;
    this.processingDelay = 1000; // Délai d'attente plus long (1 seconde)
    this.isCollecting = false; // Indique si nous sommes en train de collecter des événements
    this.collectingStartTime = 0; // Timestamp du début de la collecte
    
    // Mapping complet des widgets par priorité (du plus spécifique au plus générique)
    // Priority 0 = très spécifique, plus grand = plus générique
    this.widgetPriorityMap = {
      // Vues très spécifiques
      'plan 2d': 1,
      'plan 3d': 1,
      '2d-plan': 1,
      '3d-plan': 1,
      'pedestrian': 2, // Vue piéton est très spécifique
      'vue drone': 3,
      'drone-view': 3,
      'drone-floor-view': 3,
      'visite virtuelle': 4,
      'virtual-tour': 4,
      'tour-model': 4,
      
      // Vues de niveau moyen
      'lot': 9,
      'plot': 9,
      'plan de masse': 10,
      'ground-plan': 10,
      'vue etages': 11,
      'cutaway': 11,
      'cutaway-view': 11,
      'maquette 3d': 13,
      'digital-model': 13,
      'carrousel': 14,
      'carousel': 14,
      'carousel-lot': 14,
      'plot-carousel': 14,
      'carte': 15,
      'map': 15,
      'map-view': 15,
      
      // Vues plus générales
      'visite configurable': 20,
      'configurable-tour': 20,
      'configurable-tours': 20,
      'conf-tour-models': 20,
      'visite temoins': 21,
      'models': 21,
      'tour-models': 21,
      'documentation': 22,
      'perspective': 23,
      'animation': 24,
      'video': 25,
      'district-video': 25,
      'realite augmentee': 26,
      'augmented-reality': 26,
      'grille de lot': 27,
      'plot-grid': 27,
      
      // Vues générales
      'aerial': 40,      // Vue aérienne
      'general': 41,     // Vue générale
      'general-view': 41,
      
      // Le plus générique
      'megawidget': 100,
      'programme': 101,
      'program': 101
    };
  }

  // Mapping des widgets selon le document
  getWidgetName(routeName, widget) {
    const widgetMapping = {
      'programme': 'Megawidget',
      'program': 'Megawidget',
      'video': 'Video',
      'district-video': 'Video',
      'map': 'Carte',
      'carousel': 'Carrousel',
      'plot-carousel': 'Carrousel',
      'animation': 'Animation',
      'perspective': 'Perspective',
      'tour-models': 'Visite temoins',
      'tour-model': 'Visite virtuelle',
      'drone-view': 'Vue drone',
      'drone-floor-view': 'Vue drone',
      'ground-plan': 'Plan de masse',
      'general-view': 'Vue generale',
      'digital-model': 'Maquette 3D',
      'cutaway-view': 'Vue etages',
      'three-d-plan': 'Plan 3D',
      'two-d-plan': 'Plan 2D',
      'plot': 'Lot',
      'conf-tour-models': 'Visite configurable',
      'configurable-tour': 'Visite configurable',
      'documentation': 'Documentation',
      'plot-grid': 'Grille de lot',
      'augmented-reality': 'Realite augmentee'
    };
    return widgetMapping[routeName] || widgetMapping[widget] || widget;
  }

  // Mapping des catégories de widgets pour le megawidget
  getWidgetCategory(dataType, widget, mode) {
    if (widget === 'program' || widget === 'programme') {
      const categoryMapping = {
        'digital-model': 'Maquette 3D',
        'cutaway': 'Vue etages',
        'plot': 'Lot',
        'virtual-tour': 'Visite virtuelle',
        'configurable-tour': 'Visite configurable',
        'models': 'Visite temoins',
        'conf-models': 'Visite configurable',
        'map-view': 'Carte',
        'carousel': 'Carrousel',
        'perspective': 'Perspective',
        'drone-view': 'Vue drone',
        'district-video': 'Video',
        'animation': 'Animation',
        'documentation': 'Documentation',
        'plot-grid': 'Grille de lot',
        '2d-plan': 'Plan 2D',
        '3d-plan': 'Plan 3D'
      };
      return categoryMapping[dataType] || dataType || '';
    }
    
    // Pour les widgets standalone, utiliser le mode si disponible
    if (mode) {
      if (mode === 'general') return 'Vue generale';
      if (mode === 'digital-model') return 'Maquette 3D';
      if (mode === 'ground-plan') return 'Plan de masse';
    }
    
    return '';
  }

  // Récupération des paramètres depuis l'URL
  getTrackingParams() {
    const url = new URL(window.location.href);
    return {
      patternUrl: url.pathname + url.search,
      id_programme: url.searchParams.get('id') || '',
      id_lot: url.searchParams.get('lot') || url.searchParams.get('lotId') || '',
      referrer: url.searchParams.get('ref') || url.searchParams.get('referrer') || url.searchParams.get('source') || ''
    };
  }

  // Nettoyage et formatage des variantes
  formatVariantes(variantes) {
    if (!variantes) return '';
    
    if (Array.isArray(variantes)) {
      return variantes.join('_');
    }
    
    if (typeof variantes === 'object') {
      return Object.keys(variantes).map(key => `${key}-${variantes[key]}`).join('_');
    }
    
    return String(variantes);
  }

  // Vérifier si deux événements appartiennent au même groupe d'action utilisateur
  // (par exemple, clic sur un élément qui déclenche plusieurs événements)
  areInSameEventGroup(data1, data2) {
    if (!data1 || !data2) return false;
    
    // On considère que deux événements sont dans le même groupe s'ils ont:
    // 1. Le même ID de programme
    // 2. Le même ID de lot (s'il existe)
    // 3. Le même type d'événement
    return (
      data1.event === data2.event &&
      data1.id_programme === data2.id_programme &&
      (data1.id_lot === data2.id_lot || (data1.id_lot === '' && data2.id_lot === ''))
    );
  }
  
  // Détermine la priorité d'un widget en fonction de sa spécificité
  // Plus la valeur retournée est petite, plus le widget est spécifique
  getWidgetPriority(widgetName) {
    if (!widgetName) return 999; // Priorité la plus basse pour les noms vides
    
    const lowerName = widgetName.toLowerCase();
    
    // Rechercher une correspondance exacte d'abord
    if (this.widgetPriorityMap[lowerName] !== undefined) {
      return this.widgetPriorityMap[lowerName];
    }
    
    // Sinon, rechercher une correspondance partielle
    let bestPriority = 999;
    let bestMatch = null;
    
    Object.keys(this.widgetPriorityMap).forEach(key => {
      if (lowerName.includes(key) || key.includes(lowerName)) {
        const priority = this.widgetPriorityMap[key];
        if (priority < bestPriority) {
          bestPriority = priority;
          bestMatch = key;
        }
      }
    });

    if (bestMatch) {
      return bestPriority;
    }

    // Priorité par défaut pour les widgets inconnus
    return 50;
  }
  
  // Génère une clé unique pour un groupe d'événements
  getEventGroupKey(data) {
    // Clé simplifiée pour le groupement global
    return `${data.event}_${data.id_programme}`;
  }

  // Traite tous les événements en attente, en ne conservant que les plus spécifiques
  processPendingEvents() {
    // Fin de la période de collecte
    this.isCollecting = false;
    
    // Annuler le timeout existant s'il y en a un
    if (this.processingTimeout) {
      clearTimeout(this.processingTimeout);
      this.processingTimeout = null;
    }
    
    // Si aucun événement n'a été collecté, rien à faire
    if (this.allPendingEvents.length === 0) {
      return;
    }

    // Grouper les événements par clef
    const groupedEvents = {};
    
    // Parcourir tous les événements collectés
    this.allPendingEvents.forEach(event => {
      const key = this.getEventGroupKey(event);
      if (!groupedEvents[key]) {
        groupedEvents[key] = [];
      }
      groupedEvents[key].push(event);
    });
    
    // Traiter chaque groupe d'événements
    Object.keys(groupedEvents).forEach(groupKey => {
      const eventsGroup = groupedEvents[groupKey];

      // Trier les événements par priorité (du plus spécifique au moins spécifique)
      eventsGroup.sort((a, b) => {
        const priorityA = this.getWidgetPriority(a.widget_name);
        const priorityB = this.getWidgetPriority(b.widget_name);
        return priorityA - priorityB;
      });

      // Envoyer uniquement l'événement le plus spécifique (le premier après tri)
      const mostSpecificEvent = eventsGroup[0];

      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push(mostSpecificEvent);
      }
    });
    
    // Réinitialiser la collection d'événements
    this.allPendingEvents = [];
  }
  
  // Démarrer une nouvelle période de collecte d'événements
  startCollectingEvents() {
    // Si nous ne sommes pas déjà en train de collecter des événements
    if (!this.isCollecting) {
      this.isCollecting = true;
      this.collectingStartTime = Date.now();
      this.allPendingEvents = [];

      // Prévoir le traitement des événements après un délai
      if (this.processingTimeout) {
        clearTimeout(this.processingTimeout);
      }

      this.processingTimeout = setTimeout(() => {
        this.processPendingEvents();
      }, this.processingDelay);
    }
    // Si nous sommes déjà en train de collecter, prolonger le délai si nécessaire
    else {
      const elapsed = Date.now() - this.collectingStartTime;

      // Si le temps écoulé approche de notre délai, prolonger la période de collecte
      if (elapsed > (this.processingDelay * 0.7)) {
        // Annuler le timeout existant
        if (this.processingTimeout) {
          clearTimeout(this.processingTimeout);
        }

        // Prévoir un nouveau traitement
        this.processingTimeout = setTimeout(() => {
          this.processPendingEvents();
        }, this.processingDelay);
      }
    }
  }
  
  // Envoi de l'événement de tracking avec regroupement global
  sendWidgetDisplay(params) {
    try {
      if (typeof window !== 'undefined' && window.dataLayer) {
        // Récupérer les paramètres d'URL
        const urlParams = this.getTrackingParams();
        
        // Si un ID de lot est fourni en paramètre, l'utiliser en priorité
        if (params.data) {
          const lotId = params.data.plotId;
          if (lotId && !urlParams.id_lot) {
            urlParams.id_lot = lotId;
          }
        }
        
        const trackingData = {
          event: this.defaultEvent,
          widget_name: params.widget_name || '',
          widget_category: params.widget_category || '',
          view: params.view || '',
          typo: params.typo || '',
          variante: this.formatVariantes(params.variante) || '',
          piece: params.piece || '',
          timestamp: Date.now(),
          ...urlParams
        };
        
        // Nettoyer les valeurs null ou undefined
        Object.keys(trackingData).forEach(key => {
          if (trackingData[key] === null || trackingData[key] === undefined) {
            trackingData[key] = '';
          }
        });
        
        // Démarrer ou prolonger la période de collecte
        this.startCollectingEvents();

        // Ajouter l'événement à la collection
        this.allPendingEvents.push(trackingData);
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi du tracking:', error);
    }
  }

  // Méthode utilitaire pour extraire des informations du data
  extractDataInfo(data, key) {
    if (!data) return '';
    
    // Pour les détails d'un plot/lot
    if (data.details && Array.isArray(data.details)) {
      const detail = data.details.find(d => d.id === key);
      return detail ? detail.value : '';
    }
    
    // Pour les items actifs
    if (data.items && Array.isArray(data.items)) {
      const activeItem = data.items.find(item => item.active);
      if (activeItem && activeItem.details) {
        const detail = activeItem.details.find(d => d.id === key);
        return detail ? detail.value : '';
      }
    }
    
    // Accès direct à la propriété
    return data[key] || '';
  }

  // Méthode pour obtenir le nom de la vue actuelle
  getCurrentView(data, widget, mode) {
    if (!data) return '';
    
    switch (widget) {
      case 'carousel':
      case 'plot-carousel':
        return (data.currentImage && data.currentImage.name) || (data.activeImage && data.activeImage.name) || '';
        
      case 'digital-model':
      case 'general-view':
        if (data.currentView) return data.currentView;
        if (data.selectedView) return data.selectedView;
        if (mode === 'general') return 'Vue generale';
        if (mode === 'digital-model') return 'Maquette 3D';
        return '';
        
      case 'cutaway-view':
        return data.currentFloor || data.selectedFloor || '';
        
      case 'perspective':
        return data.currentPerspective || '';
        
      case 'drone-view':
        return data.currentDroneView || '';
        
      default:
        return data.currentView || data.view || '';
    }
  }

  // Méthode pour obtenir la typologie
  getCurrentTypo(data) {
    return this.extractDataInfo(data, 'category') || 
           this.extractDataInfo(data, 'typologie') || 
           this.extractDataInfo(data, 'type') || '';
  }

  // Méthode pour obtenir les variantes
  getCurrentVariante(data) {
    const variantes = data.selectedVariantes || data.variantes || data.options || {};
    return this.formatVariantes(variantes);
  }

  // Méthode pour obtenir la pièce actuelle
  getCurrentPiece(data) {
    return data.currentRoom || 
           data.selectedRoom || 
           data.activePiece || 
           this.extractDataInfo(data, 'room') || '';
  }
}

export default new TrackingService();


// WEBPACK FOOTER //
// ./src/services/TrackingService.js